import * as React from "react";
import { IAllMatters, IAllUsers } from "../../../common/models/fillData";
import { IOPPlayer } from "../../../common/models/player";
import { PlaceholderSlot } from "../FillPlaceholders/SearchAndReplace";

/**
 * Context store for everything Fill Placeholders has loaded or selected.
 * It exists because tab switches unmount the screen — mounting providers in
 * index.tsx (outside the tab switcher) lets selections and fetched data
 * survive. The two `loaded…Id` fields record which attorney/matter the
 * cached data belongs to, so the screen's effects skip refetching on
 * remount unless the selection actually changed.
 */
interface FillPlaceholdersStateValue {
  users: IAllUsers[];
  setUsers: React.Dispatch<React.SetStateAction<IAllUsers[]>>;
  attorneys: IAllUsers[];
  setAttorneys: React.Dispatch<React.SetStateAction<IAllUsers[]>>;
  selectedAttorney: IAllUsers | undefined;
  setSelectedAttorney: React.Dispatch<React.SetStateAction<IAllUsers | undefined>>;
  selectedWriter: IAllUsers | undefined;
  setSelectedWriter: React.Dispatch<React.SetStateAction<IAllUsers | undefined>>;
  matters: IAllMatters[];
  setMatters: React.Dispatch<React.SetStateAction<IAllMatters[]>>;
  selectedMatterOption: IAllMatters | undefined;
  setSelectedMatterOption: React.Dispatch<React.SetStateAction<IAllMatters | undefined>>;
  selectedMatter: object | undefined;
  setSelectedMatter: React.Dispatch<React.SetStateAction<object | undefined>>;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  clients: any[];
  setClients: React.Dispatch<React.SetStateAction<any[]>>;
  agents: any[];
  setAgents: React.Dispatch<React.SetStateAction<any[]>>;
  slots: PlaceholderSlot[];
  setSlots: React.Dispatch<React.SetStateAction<PlaceholderSlot[]>>;
  slotSelections: Record<string, IOPPlayer>;
  setSlotSelections: React.Dispatch<React.SetStateAction<Record<string, IOPPlayer>>>;
  // Tracking which inputs have been loaded against, to skip redundant re-fetches on remount.
  loadedMattersForAttorneyId: number | undefined;
  setLoadedMattersForAttorneyId: React.Dispatch<React.SetStateAction<number | undefined>>;
  loadedContextForOptionId: number | undefined;
  setLoadedContextForOptionId: React.Dispatch<React.SetStateAction<number | undefined>>;
}

const FillPlaceholdersContext = React.createContext<FillPlaceholdersStateValue | undefined>(undefined);

export const FillPlaceholdersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [users, setUsers] = React.useState<IAllUsers[]>([]);
  const [attorneys, setAttorneys] = React.useState<IAllUsers[]>([]);
  const [selectedAttorney, setSelectedAttorney] = React.useState<IAllUsers | undefined>(undefined);
  const [selectedWriter, setSelectedWriter] = React.useState<IAllUsers | undefined>(undefined);
  const [matters, setMatters] = React.useState<IAllMatters[]>([]);
  const [selectedMatterOption, setSelectedMatterOption] = React.useState<IAllMatters | undefined>(undefined);
  const [selectedMatter, setSelectedMatter] = React.useState<object | undefined>(undefined);
  const [query, setQuery] = React.useState<string>("");
  const [clients, setClients] = React.useState<any[]>([]);
  const [agents, setAgents] = React.useState<any[]>([]);
  const [slots, setSlots] = React.useState<PlaceholderSlot[]>([]);
  const [slotSelections, setSlotSelections] = React.useState<Record<string, IOPPlayer>>({});
  const [loadedMattersForAttorneyId, setLoadedMattersForAttorneyId] = React.useState<number | undefined>(undefined);
  const [loadedContextForOptionId, setLoadedContextForOptionId] = React.useState<number | undefined>(undefined);

  const value: FillPlaceholdersStateValue = {
    users, setUsers,
    attorneys, setAttorneys,
    selectedAttorney, setSelectedAttorney,
    selectedWriter, setSelectedWriter,
    matters, setMatters,
    selectedMatterOption, setSelectedMatterOption,
    selectedMatter, setSelectedMatter,
    query, setQuery,
    clients, setClients,
    agents, setAgents,
    slots, setSlots,
    slotSelections, setSlotSelections,
    loadedMattersForAttorneyId, setLoadedMattersForAttorneyId,
    loadedContextForOptionId, setLoadedContextForOptionId,
  };

  return (
    <FillPlaceholdersContext.Provider value={value}>
      {children}
    </FillPlaceholdersContext.Provider>
  );
};

export const useFillPlaceholdersState = (): FillPlaceholdersStateValue => {
  const ctx = React.useContext(FillPlaceholdersContext);
  if (!ctx) throw new Error("useFillPlaceholdersState must be used inside <FillPlaceholdersProvider>");
  return ctx;
};
